// Read-only map queries for presentation: a scratch World whose map state mirrors the FrameView, so
// `MapDef.groundAt` / `solids` answer for what is on screen (fallen tiles, wedges, hazard poses).
import { MAX_BEANS, NO_SLOT } from '@shared/constants';
import { ItemKind, MapId, ModeId, MutatorId } from '@shared/ids';
import { MAPS } from '@shared/maps/index';
import type { MapDef } from '@shared/maps/mapTypes';
import { createWorld } from '@shared/sim/world';
import type { RoundConfig, World } from '@shared/types';
import type { FrameView } from '../../game/FrameView';

export interface MapProbe {
  readonly def: MapDef;
  readonly world: World;
  /** Mirrors the frame's map state and round tick into the scratch world. */
  sync(view: FrameView): void;
  groundAt(x: number, y: number): number;
}

/** A bean-less RoundConfig for the map: enough for `createWorld` to initialise map state. */
export function probeConfig(mapId: MapId, modeId: ModeId, seed: number): RoundConfig {
  return {
    mapId, modeId, mutator: MutatorId.NONE, seed, golden: false, itemsOn: false, teamsOn: false,
    activeSlots: new Array<boolean>(MAX_BEANS).fill(false),
    teams: new Array<number>(MAX_BEANS).fill(0),
    matchScores: new Array<number>(MAX_BEANS).fill(0),
    crownedSlot: NO_SLOT,
    startItems: new Array<ItemKind>(MAX_BEANS).fill(ItemKind.NONE),
  };
}

/** The mode a map is normally played in; map queries do not depend on it. */
export function defaultModeFor(mapId: MapId): ModeId {
  return mapId === MapId.LOBBY ? ModeId.LOBBY : mapId === MapId.PODIUM ? ModeId.PODIUM : ModeId.ROYALE;
}

export function createMapProbe(mapId: MapId, modeId: ModeId, seed: number): MapProbe {
  const def = MAPS[mapId];
  const world = createWorld(probeConfig(mapId, modeId, seed), 0, false);
  return {
    def, world,
    sync(view) {
      world.map = view.map;
      world.tick = Math.floor(view.roundTick);
    },
    groundAt(x, y) {
      return def.groundAt(world, x, y);
    },
  };
}
